import { useState } from "react";
import { Box } from "@mui/material";
import BackButton from "components/lib/backButton";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import { useEnabledMarkets } from "core/selectors/marketSelectors";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { FormData } from "core/types";
import { useCurrentUser } from "hooks/userHooks";
import PatientDetails from "./patientDetails";
import RecipientDetails from "./recipientDetails";
import ReferralFooter from "./referralFooter";
import {
  StyledDescriptionBody,
  StyledRecipientDetails,
  StyledReferralInstructions,
} from "./referralForm.styles";
import { ReferralWrapper, FormContainer } from "./styled";
import TypeSelector from "./typeSelector";
import { usePsychologistUser } from "hooks";
import { TextField, useSnackbar } from "@mindlercare/ui-web";
import { useSendSafelyInit } from "hooks/useSendSafelyInit";

const getResultMessageKey = (type: string, success: boolean) => {
  if (type === CONSTANTS.REFERRAL) {
    return success ? "REFERRAL_SENT" : "REFERRAL_SEND_FAIL";
  }
  if (type === CONSTANTS.CERTIFICATE) {
    return success ? "CERTIFICATE_SENT" : "CERTIFICATE_SEND_FAIL";
  }
  return "";
};

export const ReferralForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ patientUserId: string }>();
  const { user } = useCurrentUser();
  const { enqueueSnackbar } = useSnackbar();
  const markets = useEnabledMarkets();
  const psychologistId =
    usePsychologistUser().psychologist?.psychologistId ?? null;

  const sendSafely = useSendSafelyInit();

  const [formData, setFormData] = useState<FormData>({
    type: "",
    name_clinic: "",
    address: "",
    referral_certificate: "",
  });

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(params.patientUserId),
      },
      {
        enabled: !!params.patientUserId,
      },
    ),
  );

  const getSmallPrint = () => {
    switch (formData.type) {
      case CONSTANTS.REFERRAL:
        return t("REFERRAL_DETAILS");
      case CONSTANTS.CERTIFICATE:
        return t("CERTIFICATE_DETAILS");
      default:
        return null;
    }
  };

  const onCancel = () => {
    const actionTabs = CONSTANTS.PATIENT_CARD_TABS.ACTIONS.toLowerCase();
    navigate({
      pathname: `/patients/psychologist/${psychologistId}/patient/${patient?.userId}/${actionTabs}`,
    });
  };

  const onFormFieldChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSendResult = ({
    success,
    redirect,
  }: {
    success: boolean;
    redirect: boolean;
  }) => {
    enqueueSnackbar({
      text: t(getResultMessageKey(formData.type, success)),
      isError: !success,
    });
    if (redirect) onCancel();
  };

  const isFormIncomplete = Object.values(formData).some((value) => !value);
  const isSubmitDisabled = isFormIncomplete || !sendSafely.isReady;

  const onSubmit = () => {
    if (!formData.type || !patient || !user) return;
    sendSafely.submitHandler({
      formData,
      patient,
      user,
      markets,
      onResult: handleSendResult,
    });
  };

  return (
    <ReferralWrapper>
      <Box sx={{ marginBottom: "24px" }}>
        <BackButton />
      </Box>
      <Typography variant="h2">
        {t("CREATE_CERTIFICATE_OR_REFERRAL")}
      </Typography>
      <FormContainer>
        <TypeSelector
          formData={formData}
          onFormFieldChange={onFormFieldChange}
          getSmallPrint={getSmallPrint}
        />
        <PatientDetails formData={formData} patient={patient} />
        <RecipientDetails
          formData={formData}
          onFormFieldChange={onFormFieldChange}
        />

        <StyledReferralInstructions>
          <StyledRecipientDetails variant="h4">
            {t("CERTIFICATE_REFERRAL")}
          </StyledRecipientDetails>
          <StyledDescriptionBody variant="body-x-small">
            {getSmallPrint()}
          </StyledDescriptionBody>
          <TextField
            onChange={(e) =>
              onFormFieldChange("referral_certificate", e.target.value)
            }
            value={formData.referral_certificate}
            disabled={!formData.type}
            hideSensitiveData={false}
            placeholder={t("WRITE_CERTIFICATE_OR_REFERRAL")}
            multiline
          />
          {sendSafely.Component}
        </StyledReferralInstructions>
        <ReferralFooter
          user={user}
          onSubmit={onSubmit}
          onCancel={onCancel}
          submitDisabled={isSubmitDisabled}
        />
      </FormContainer>
    </ReferralWrapper>
  );
};
